import { CustomerTagRecord } from "@redotech/redo-common-model-tags-common/customer-tag-record";
import { CreateCustomerTagBody } from "@redotech/redo-common-model/customer-tags/create-customer-tag-body";
import { PatchCustomerTagBody } from "@redotech/redo-common-model/customer-tags/patch-customer-tag-body";
import { CustomerTagWithId } from "@redotech/redo-customer-common/customer";
import { RedoMerchantClient } from "../client";

export interface CustomerTagSearchResult {
  tags: CustomerTagRecord[];
  total: number;
}

async function fetchCustomerTagsOfTeam(
  client: RedoMerchantClient,
  {
    teamId,
    query,
    limit,
    ids,
  }: { teamId: string; query?: string; limit?: number; ids?: string[] },
  signal?: AbortSignal,
): Promise<CustomerTagSearchResult> {
  const response = await client.client.get(
    `customer-tags/teams/${teamId}/customer-tags`,
    {
      headers: client.authorization(),
      params: { query, limit, ids: ids?.length ? ids.join(",") : undefined },
      signal,
    },
  );
  return response.data;
}

/**
 * Loads up to `limit` matching tags and returns the true total number of
 * matches so callers can show a "more results" hint.
 */
export async function searchCustomerTagsOfTeam(
  client: RedoMerchantClient,
  { teamId, query, limit }: { teamId: string; query?: string; limit: number },
  signal?: AbortSignal,
): Promise<CustomerTagSearchResult> {
  return fetchCustomerTagsOfTeam(client, { teamId, query, limit }, signal);
}

/**
 * Resolves specific tags by id — used to render already-selected tags
 */
export async function resolveCustomerTagsByIds(
  client: RedoMerchantClient,
  { teamId, ids }: { teamId: string; ids: string[] },
  signal?: AbortSignal,
): Promise<CustomerTagRecord[]> {
  if (ids.length === 0) {
    return [];
  }
  const { tags } = await fetchCustomerTagsOfTeam(
    client,
    { teamId, ids },
    signal,
  );
  return tags;
}

/**
 * @param reloadCallback forces the caller to think about what needs reloading after updating a tag
 */
export async function patchCustomerTag(
  client: RedoMerchantClient,
  tagId: string,
  body: PatchCustomerTagBody,
  reloadCallback: () => void | Promise<void>,
  signal?: AbortSignal,
): Promise<CustomerTagRecord> {
  const response = await client.client.patch(`customer-tags/${tagId}`, body, {
    headers: client.authorization(),
    signal,
  });
  await reloadCallback();
  return response.data;
}

/**
 * @param reloadCallback forces the caller to think about what needs reloading after making a tag
 */
export async function postCustomerTag(
  client: RedoMerchantClient,
  body: CreateCustomerTagBody,
  reloadCallback: () => void | Promise<void>,
  signal?: AbortSignal,
): Promise<CustomerTagRecord> {
  const response = await client.client.post("customer-tags", body, {
    headers: client.authorization(),
    signal,
  });
  await reloadCallback();
  return response.data;
}

/**
 * PUT /customers/:id/tags
 */
export async function setCustomerTags(
  client: RedoMerchantClient,
  {
    customerId,
    tags,
    signal,
  }: { customerId: string; tags: CustomerTagWithId[]; signal?: AbortSignal },
): Promise<void> {
  await client.client.put(
    `customers/${encodeURIComponent(customerId)}/tags`,
    { tags },
    { headers: client.authorization(), signal },
  );
}
